/* eslint-disable import/first */
import React, { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import AppSettings from './AppSettings';
// Composants
import { Toaster } from 'react-hot-toast';
import UseAnimations from 'react-useanimations';
import GlobalWebSocket from './components/Utils/GlobalWebSocket';
import SyncManager from './components/Utils/SyncManager';
const MobileNavbar = lazy(() => import('./components/Navbars/MobileNavbar'));
const BrowserNavbar = lazy(() => import('./components/Navbars/BrowserNavbar'));
const Home = lazy(() => import('./components/Pages/Home'));
const ProjectMap = lazy(() => import('./components/Pages/ProjectMap'));
const Map = lazy(() => import('./components/Pages/Map'));
const Signup = lazy(() => import('./components/Pages/Signup'));
const Login = lazy(() => import('./components/Pages/Login'));
const ForgotPassword = lazy(() => import('./components/Pages/ForgotPassword'));
const AccountPanel = lazy(() => import('./components/Pages/AccountPanel'));
const ResetPassword = lazy(() => import('./components/Pages/ResetPassword'));
const ConfirmEmail = lazy(() => import('./components/Pages/ConfirmEmail'));
const AdminPanel = lazy(() => import('./components/Administration/AdminPanel'));
const Tutorial = lazy(() => import('./components/Utils/Tutorial'));
const HelpdeskButton = lazy(() => import('./components/Utils/HelpdeskButton'));
const TermsOfUse = lazy(() => import('./components/Pages/TermsOfUse/TermsOfUse'));
const ChromeModal = lazy(() => import('./components/Utils/ChromeModal'));
const OfflineModal = lazy(() => import('./components/Utils/OfflineModal'));
const LogoutGuardModal = lazy(() => import('./components/Utils/LogoutGuardModal'));
const StudentActivation = lazy(() => import('./components/Pages/StudentActivation'));
const OAuthConsent = lazy(() => import('./components/Pages/OAuthConsent'));
const LoginAsBand = lazy(() => import('./components/Utils/LoginAsBand'));
const SubscriptionLimitsBand = lazy(() => import('./components/Utils/SubscriptionLimitsBand'));
const FreeBand = lazy(() => import('./components/Utils/FreeBand'));
const ViewProjectAsBand = lazy(() => import('./components/Utils/ViewProjectAsBand'));
const PublicPreviewBand = lazy(() => import('./components/Utils/PublicPreviewBand'));
const OfflineBand = lazy(() => import('./components/Utils/OfflineBand'));
const UpdateBand = lazy(() => import('./components/Utils/UpdateBand'));
const AIScanWarning = lazy(() => import('./components/Utils/AIScanWarning'));
const OrganizationList = lazy(() => import('./components/Lists/OrganizationList'));
// Librairies
import { isBrowser, isMobileOnly, isTablet, withOrientationChange } from 'react-device-detect';
import Cookies from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';
import Axios from 'axios';
import screenfull from 'screenfull';
import i18n from './locales/i18n';
import { library } from '@fortawesome/fontawesome-svg-core';
import * as Icons from '@fortawesome/pro-solid-svg-icons';
// Redux
import { connect } from 'react-redux';
import { setLoginAsData } from './actionCreators/adminActions';
import { setUserInfos, setActiveOrganization, setOrganizations, setFavoriteTools, setOrganizationCustomFields, setCustomFieldCategories, setHomeInfos, setPriceLists, setThemes } from './actionCreators/usersActions';
import { setUpdateApp, setIsKeyboardOpen, setIsDarkTheme, setFormulas, setProjections, setCurrencies, setProjectListLoadingStatus, toggleToolbarExpand } from './actionCreators/appActions';
import { setCustomCharts, setFilterLists, setCurrentFolderState } from './actionCreators/componentsActions';
import { setDefaultFieldCategories, setCustomFields, setPhotosGalleries, setFilesGalleries } from './actionCreators/elementsActions';
import {
  setEssences, setCoverTypes, setInteractions, setMicroHabitats, setRootSymptoms, setCollarSymptoms, setTrunkSymptoms, setBranchSymptoms, setLeafSymptoms, setPathogens, setPests, setEpiphytes,
  setVigors, setDeathCauses, setHealthReviews, setOntogenicStages, setArchiStages, setArchiStates, setRisks, setTippingRisks, setOrganCalibers, setTargets, setTreePorts, setPlantationTypes, setPlantationCoefficients,
  setSituationCoefficients, setPatrimonialCoefficients,
  setEvents
} from './actionCreators/treesActions';
import {
  setSpaceFunctions, setSpaceTypes, setSpaceTypeCategories, setRunoffCoefficients, setManagementClasses, setDominantCompositions
} from './actionCreators/greenSpacesActions';
import { setConditions, setFurnitureTypes } from './actionCreators/furnituresActions';
import { setActions, setProjects, setProjectsCustomFields, setViewProjectAsData } from './actionCreators/projectsActions';
// Ressources
import alert from 'react-useanimations/lib/alertCircle';
// Semantic UI
import { Dimmer, Loader, Header, Progress, Grid, Message } from 'semantic-ui-react';
// Services
import TreesService from './services/TreesService';
import GreenSpacesService from './services/GreenSpacesService';
import FurnituresService from './services/FurnituresService';
import ActionsService from './services/ActionsService';
import ParametersService from './services/ParametersService';
import UsersService from './services/UsersService';
import FormulasService from './services/FormulasService';
import ProjectsService from './services/ProjectsService';
import CustomFieldsService from './services/CustomFieldsService';
import PriceListsService from './services/PriceListsService';
import OrganizationsService from './services/OrganizationsService';
// Styles
import './styles/app.css';
// Utils
import StylesUtil from './utils/StylesUtil';
import ThemesUtil from './utils/ThemesUtil';
import LanguagesUtil from './utils/LanguagesUtil';
import { initTabularMenuIndicator } from './utils/TabularMenuIndicatorUtil';
import OrganizationPanel from './components/Pages/OrganizationPanel';
import EventsService from './services/EventsService';
import RealtimeUtil from './utils/RealtimeUtil';
import { getOfflineServices, getCurrentUserId, deleteOfflineData } from './utils/offline/OfflineServices';
import { setPilotOrganizationIds } from './utils/offline/OfflineFlag';
import { isOnline as isApiOnline } from './utils/offline/Connectivity';
import { isProjectRefreshActive } from './utils/offline/OfflineRead';
import { showActionToast } from './utils/ToastsUtil';

class App extends React.Component {
  state = {
    activeItem: 'map',
    logged: false,
    token: '',
    projectListVisible: false,
    numberOfListsToLoad: 34,
    numberOfListsLoaded: 0,
    shouldRenderPage: false,
    loadingMessage: i18n.t("Chargement des ressources nécessaires..."),
    errorWhileLoading: false,
    isUnderMaintenance: false,
    subscriptionLoaded: false,
    isFullScreen: false,
    pilotLoadedAt: null,
    renderDefaultMap: false,
    openProjectCreation: false,
    isAIWarningVisible: false,
    showCurrentFolder: false,
    newOrganizationIds: [],
    logoutGuardCounts: null
  }

  render() {
    const { projectListVisible, shouldRenderPage, activeItem, errorWhileLoading, isFullScreen, renderDefaultMap, isAIWarningVisible, showCurrentFolder, newOrganizationIds } = this.state;
    const isTutorial = this.props.userInfos ? (!this.props.userInfos.hasOwnProperty('lastLoginDate') || !this.props.userInfos.lastLoginDate || ['false', undefined].includes(new Cookies().get('isTutorialDone'))) : false;

    return (
      <div className='App'>
        <Helmet>
          {isMobileOnly && <link rel='stylesheet' href='/mobile.css' />}
          {isTablet && <link rel='stylesheet' href='/tablet.css' />}
          {!isMobileOnly && !isTablet && <link rel='stylesheet' href='/computer.css' />}
          <link rel='stylesheet' href={this.props.isDarkTheme ? '/darktheme.css' : '/lighttheme.css'} />
        </Helmet>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {shouldRenderPage ? (
            <BrowserRouter>
              <Suspense>
                <LoginAsBand logUser={this.logUser} />
                {/* Dans le BrowserRouter : la modale navigue elle-même vers /login */}
                <LogoutGuardModal
                  counts={this.state.logoutGuardCounts}
                  onCancel={() => this.setState({ logoutGuardCounts: null })}
                  onSync={() => {
                    this.setState({ logoutGuardCounts: null });
                    getOfflineServices()?.engine?.trigger({ force: true });
                  }}
                  onForceLogout={() => {
                    this.props.setCurrentFolderState(null);
                    this.logoutAndClearOfflineData(true);
                  }}
                />
                <SubscriptionLimitsBand />
                <FreeBand />
                <ViewProjectAsBand />
                <PublicPreviewBand />
              </Suspense>
              {!isFullScreen && (
                <Suspense>
                  {isBrowser
                    ? activeItem !== 'adminPanel' && <BrowserNavbar changeActiveItem={this.changeActiveItem} activeItem={activeItem} showProjectList={this.showProjectList} logged={this.state.logged} logUser={this.logUser} requestLogout={this.requestLogout} />
                    : <MobileNavbar changeActiveItem={this.changeActiveItem} activeItem={activeItem} showProjectList={this.showProjectList} logged={this.state.logged} logUser={this.logUser} requestLogout={this.requestLogout} />}
                </Suspense>
              )}
              <Suspense
                fallback={
                  <Dimmer active style={StylesUtil.getMapStyles().dimmerStyle}>
                    <Loader />
                  </Dimmer>
                }
              >
                <Switch>
                  {/* Pages qui créent ou retirent des accès (connexion, jetons, assistants IA, administration) jamais affichées dans un cadre, même
                      atteintes sans rechargement depuis une page encadrée (les en-têtes de nginx ne s'appliquent qu'au chargement) */}
                  {window.top !== window.self &&
                    <Route path={['/login', '/signup', '/adminPanel', '/organizationPanel', '/accountPanel', '/resetPassword', '/oauth']} render={() =>
                      <Grid textAlign='center' style={{ margin: '0', flexGrow: 1 }} verticalAlign='middle'>
                        <Grid.Column style={{ maxWidth: 560 }}>
                          <Message error role='alert' header={i18n.t("Cette page ne peut pas être affichée dans une autre application")} />
                        </Grid.Column>
                      </Grid>
                    } />}
                  <Route
                    exact path='/'
                    render={(props) =>
                      renderDefaultMap
                        ? <Map {...props} showProjectList={this.showProjectList} projectListVisible={projectListVisible} changeActiveItem={this.changeActiveItem} logged={this.state.logged} openProjectCreation={this.state.openProjectCreation} />
                        : <Home {...props} changeActiveItem={this.changeActiveItem} showProjectList={this.showProjectList} />
                    }
                  />
                  <Route
                    path='/projects/:pid/:path1?/:path2?/:path3?'
                    render={(props) => <ProjectMap {...props} key={String(this.props.project?.id || props.match.params.pid)} currentFolderState={showCurrentFolder && this.props.currentFolderState} showProjectList={this.showProjectList} projectListVisible={projectListVisible} showAIWarning={this.showAIWarning} changeActiveItem={this.changeActiveItem} logged={this.state.logged} logUser={this.logUser} />}
                  />
                  <Route path='/signup/:invitationId?' render={(props) => <Signup {...props} changeActiveItem={this.changeActiveItem} logUser={this.logUser} logged={this.state.logged} />} />
                  <Route path='/login/:token?' render={(props) => <Login {...props} changeActiveItem={this.changeActiveItem} logUser={this.logUser} logged={this.state.logged} />} />
                  <Route path='/adminPanel' render={(props) => <AdminPanel {...props} changeActiveItem={this.changeActiveItem} logUser={this.logUser} requestLogout={this.requestLogout} showProjectList={this.showProjectList} logged={this.state.logged} activeItem={activeItem} />} />
                  <Route path='/organizationPanel/:category?' render={(props) => <OrganizationPanel {...props} changeActiveItem={this.changeActiveItem} logUser={this.logUser} logged={this.state.logged} />} />
                  <Route path='/accountPanel/:category?' render={(props) => <AccountPanel {...props} changeActiveItem={this.changeActiveItem} logUser={this.logUser} logged={this.state.logged} />} />
                  <Route path='/forgotPassword' render={(props) => <ForgotPassword {...props} changeActiveItem={this.changeActiveItem} logged={this.state.logged} />} />
                  <Route path='/resetPassword' render={(props) => <ResetPassword {...props} changeActiveItem={this.changeActiveItem} logged={this.state.logged} />} />
                  <Route path='/confirmEmail' render={(props) => <ConfirmEmail {...props} changeActiveItem={this.changeActiveItem} logUser={this.logUser} logged={this.state.logged} />} />
                  <Route path='/student/:token' render={(props) => <StudentActivation {...props} logged={this.state.logged} logUser={this.logUser} />} />
                  <Route path='/termsOfUse' render={(props) => <TermsOfUse {...props} />} />
                  <Route path='/oauth/consent' render={(props) => <OAuthConsent {...props} changeActiveItem={this.changeActiveItem} logUser={this.logUser} logged={this.state.logged} />} />
                  <Redirect from='/*' to='/' />
                </Switch>
              </Suspense>
            </BrowserRouter>
          ) : (
            <Dimmer active style={StylesUtil.getMapStyles().dimmerStyle}>
              {!errorWhileLoading ? (
                <Loader
                  content={
                    <Progress
                      value={this.state.numberOfListsLoaded}
                      total={this.state.numberOfListsToLoad}
                      size='tiny'
                      style={isMobileOnly ? { width: 200 } : { width: 400 }}
                    >
                      <div style={{ color: 'white' }}>{this.state.loadingMessage}</div>
                    </Progress>
                  }
                />
              ) : (
                <Header as='h1' icon textAlign='center'>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <UseAnimations animation={alert} strokeColor='white' autoplay loop size={130} />
                  </div>
                  <Header.Content style={{ color: 'white' }}>
                    {i18n.t('Oops ! Le chargement des ressources nécessaires a échoué...')}
                  </Header.Content>
                  <Header.Subheader as='h3' style={{ color: 'white' }}>
                    {i18n.t('Veuillez vérifier votre connexion et rafraîchir la page.')}
                  </Header.Subheader>
                </Header>
              )}
            </Dimmer>
          )}
          <UpdateBand />
          <OfflineBand />
        </div>
        <Suspense>
          {isTutorial && <Tutorial />}
          <ChromeModal />
          <AIScanWarning showAIWarning={this.showAIWarning} isAIWarningVisible={isAIWarningVisible} />
          <OfflineModal />
          <HelpdeskButton />
          <OrganizationList newOrganizationIds={newOrganizationIds} close={() => this.setState({ newOrganizationIds: [] })} />
        </Suspense>
        <Toaster position='bottom-right' />
        <GlobalWebSocket changeActiveItem={this.changeActiveItem} />
        <SyncManager logged={this.state.logged} pilotLoadedAt={this.state.pilotLoadedAt} />
      </div>
    );
  }

  componentDidMount = () => {
    // setTimeout(() => console.clear(), 3500);
    ThemesUtil.applyTheme(null);
    initTabularMenuIndicator();
    this.props.setIsDarkTheme(!AppSettings.isUrbasenseUrl() && localStorage.getItem('isDarkTheme') !== 'false');
    if (new Cookies().get('oldSubscription')) setTimeout(() => new Cookies().remove('oldSubscription'), 5000);
    if (screenfull.isEnabled) screenfull.on('change', () => this.setState({ isFullScreen: screenfull.isFullscreen }));
    RealtimeUtil.init();
    // Projet du pilote ouvert : ProjectMap rafraîchit ses données et n'affiche ce toast qu'en cas d'échec
    RealtimeUtil.on('resynced', () => {
      if (!isProjectRefreshActive()) showActionToast('realtime_resynced', i18n.t("Recharger"), () => window.location.reload());
    });

    window.addEventListener('load', () => {
      let orientation = this.props.isLandscape ? 'landscape' : 'portrait';
      const totalHeight = window.screen.height;
      const totalWidth = window.screen.width;

      window.addEventListener('resize', () => {
        const newTotalHeight = window.screen.height;
        const newTotalWidth = window.screen.width;
        const newOrientation = this.props.isLandscape ? 'landscape' : 'portrait';
        const orientationChanged = orientation !== newOrientation;
        orientation = newOrientation;

        if (isMobileOnly && !orientationChanged) {
          let isKeyboardOpen = false;
          if (newTotalHeight === totalHeight && newTotalWidth === totalWidth) isKeyboardOpen = false
          else isKeyboardOpen = true;
          this.props.setIsKeyboardOpen(isKeyboardOpen);
        }
      });

      window.addEventListener('beforeunload', (event) => {
        const loginAsData = this.props.loginAsData;
        if (loginAsData && !localStorage.getItem('loginAsData')) {
          this.logUser();
          this.logUser(loginAsData);
        } else if (this.props.exitFormWithChanges) {
          event.preventDefault();
          return event.returnValue = '';
        }
      });

      if (isMobileOnly) this.props.setIsKeyboardOpen(false);
    });

    if (new Cookies().get('token')) { // Au lancement de l'application, on récupère le token de l'utilisateur si disponible
      if (new Cookies().get('tokenVersion')?.toString() === AppSettings.getTokenVersion().toString())
        this.logUser(new Cookies().get('token'));
      else {
        this.setState({ subscriptionLoaded: true });
        new Cookies().remove('token');
        new Cookies().remove('tokenVersion');
      }
    } else {
      this.waitForDatabaseVersions().then(() => this.loadList(TreesService.getEssences, 'essences', i18n.t("Chargement des essences..."), this.props.setEssences));
      this.setState({ subscriptionLoaded: true });
    }

    this.databaseVersions = null;
    this.localVersions = JSON.parse(localStorage.getItem('versions') || '{}'); // On récupère les versions des listes locales ou on crée l'objet s'il n'existe pas

    Promise.all([
      ParametersService.getVersions(),
      Axios.get('/commit.json', { headers: { 'Cache-Control': 'no-cache' } })
        .then(response => response.data)
        .catch(() => null)
    ]).then(([versions, commit]) => { // On récupère les versions depuis la base de données
      const isUnderMaintenance = versions?.find(x => x.key === 'IsUnderMaintenance').value === '1';
      if (isUnderMaintenance) this.setState({ isUnderMaintenance: true });

      if (versions || this.localVersions) {
        if (versions)
          this.databaseVersions = versions.reduce((map, obj) => {
            const versionName = obj.key.charAt(0).toLowerCase() + obj.key.substr(1).replace('Version', '');
            map[versionName] = versionName === 'app' ? obj.value : Number(obj.value);
            return map;
          }, {});
        else this.databaseVersions = this.localVersions;

        // Hors-ligne, les versions viennent du localStorage : pas de mise à jour forcée
        let shouldUpdate = !!versions && AppSettings.getAppVersion() && this.databaseVersions.app && AppSettings.getAppVersion() !== this.databaseVersions.app;
        if (!shouldUpdate && AppSettings.getAppEnv() === 'develop') {
          const currentCommit = localStorage.getItem('commitId');
          if (commit && commit.id !== currentCommit) {
            localStorage.setItem('commitId', commit.id);
            shouldUpdate = true;
          }
        }

        if (shouldUpdate) this.props.setUpdateApp();
        else {
          sessionStorage.removeItem('grality-update-attempted');
          this.loadList(TreesService.getCoverTypes, 'coverTypes', i18n.t("Chargement des types de couverture..."), this.props.setCoverTypes, 'label');
          this.loadList(TreesService.getInteractions, 'interactions', i18n.t("Chargement des interactions..."), this.props.setInteractions, 'label');
          this.loadList(EventsService.getEvents, 'events', i18n.t("Chargement des évènements..."), this.props.setEvents, 'label');
          this.loadList(TreesService.getMicroHabitats, 'microHabitats', i18n.t("Chargement des dendro-microhabitats..."), this.props.setMicroHabitats, 'label');
          this.loadList(TreesService.getRootSymptoms, 'rootSymptoms', i18n.t("Chargement des symptômes racines..."), this.props.setRootSymptoms, 'label');
          this.loadList(TreesService.getCollarSymptoms, 'collarSymptoms', i18n.t("Chargement des symptômes collet..."), this.props.setCollarSymptoms, 'label');
          this.loadList(TreesService.getTrunkSymptoms, 'trunkSymptoms', i18n.t("Chargement des symptômes tronc..."), this.props.setTrunkSymptoms, 'label');
          this.loadList(TreesService.getBranchSymptoms, 'branchSymptoms', i18n.t("Chargement des symptômes branches..."), this.props.setBranchSymptoms, 'label');
          this.loadList(TreesService.getLeafSymptoms, 'leafSymptoms', i18n.t("Chargement des symptômes feuilles..."), this.props.setLeafSymptoms, 'label');
          this.loadList(TreesService.getPathogens, 'pathogens', i18n.t("Chargement des pathogènes..."), this.props.setPathogens, 'label');
          this.loadList(TreesService.getPests, 'pests', i18n.t("Chargement des symptômes ravageurs..."), this.props.setPests, 'label');
          this.loadList(TreesService.getEpiphytes, 'epiphytes', i18n.t("Chargement des épiphytes..."), this.props.setEpiphytes, 'label');
          this.loadList(TreesService.getVigors, 'vigors', i18n.t("Chargement des vigueurs..."), this.props.setVigors);
          this.loadList(TreesService.getDeathCauses, 'deathCauses', i18n.t("Chargement des causes de mort..."), this.props.setDeathCauses);
          this.loadList(TreesService.getHealthReviews, 'healthReviews', i18n.t("Chargement des cotes sanitaires..."), this.props.setHealthReviews);
          this.loadList(TreesService.getOntogenicStages, 'ontogenicStages', i18n.t("Chargement des stades ontogéniques..."), this.props.setOntogenicStages);
          this.loadList(TreesService.getArchiStages, 'archiStages', i18n.t("Chargement des stades..."), this.props.setArchiStages);
          this.loadList(TreesService.getArchiStates, 'archiStates', i18n.t("Chargement des états..."), this.props.setArchiStates);
          this.loadList(TreesService.getRisks, 'risks', i18n.t("Chargement des risques..."), this.props.setRisks);
          this.loadList(TreesService.getTippingRisks, 'tippingRisks', i18n.t("Chargement des risques de basculement..."), this.props.setTippingRisks);
          this.loadList(TreesService.getOrganCalibers, 'organCalibers', i18n.t("Chargement des calibres d'organe instable..."), this.props.setOrganCalibers);
          this.loadList(TreesService.getTargets, 'targets', i18n.t("Chargement des cibles..."), this.props.setTargets);
          this.loadList(TreesService.getTreePorts, 'treePorts', i18n.t("Chargement des ports de l'arbre..."), this.props.setTreePorts, 'label');
          this.loadList(TreesService.getPlantationTypes, 'plantationTypes', i18n.t("Chargement des types de plantations..."), this.props.setPlantationTypes, 'label');
          this.loadList(TreesService.getPlantationCoefficients, 'plantationCoefficients', i18n.t("Chargement des coefficients de plantation..."), this.props.setPlantationCoefficients);
          this.loadList(TreesService.getSituationCoefficients, 'situationCoefficients', i18n.t("Chargement des coefficients de situation..."), this.props.setSituationCoefficients);
          this.loadList(TreesService.getPatrimonialCoefficients, 'patrimonialCoefficients', i18n.t("Chargement des coefficients patrimoniaux..."), this.props.setPatrimonialCoefficients);
          this.loadList(ActionsService.getActions, 'actions', i18n.t("Chargement des actions..."), this.props.setActions);
          this.loadList(GreenSpacesService.getSpaceFunctions, 'spaceFunctions', i18n.t("Chargement des fonctions d'espace..."), this.props.setSpaceFunctions, 'label');
          this.loadList(GreenSpacesService.getSpaceTypes, 'spaceTypes', i18n.t("Chargement des types d'espace..."), this.props.setSpaceTypes, 'label');
          this.loadList(GreenSpacesService.getSpaceTypeCategories, 'spaceTypeCategories', i18n.t("Chargement des catégories d'espace..."), this.props.setSpaceTypeCategories, 'label');
          this.loadList(GreenSpacesService.getRunoffCoefficients, 'runoffCoefficients', i18n.t("Chargement des coefficients de ruissellement..."), this.props.setRunoffCoefficients);
          this.loadList(GreenSpacesService.getManagementClasses, 'managementClasses', i18n.t("Chargement des classes de gestion..."), this.props.setManagementClasses);
          this.loadList(GreenSpacesService.getDominantCompositions, 'dominantCompositions', i18n.t("Chargement des compositions dominantes..."), this.props.setDominantCompositions, 'label');
          this.loadList(FurnituresService.getConditions, 'conditions', i18n.t("Chargement des états des mobiliers urbains..."), this.props.setConditions);
          this.loadList(FurnituresService.getFurnitureTypes, 'furnitureTypes', i18n.t("Chargement des types de mobiliers urbains..."), this.props.setFurnitureTypes, 'label');
          this.loadList(FormulasService.getFormulas, 'formulas', i18n.t("Chargement des formules..."), this.props.setFormulas);
          this.loadList(ProjectsService.getProjections, 'projections', i18n.t("Chargement des projections..."), this.props.setProjections);
          this.loadList(CustomFieldsService.getDefaultFieldCategories, 'defaultCategories', i18n.t("Chargement des catégories de champs..."), this.props.setDefaultFieldCategories);
          this.loadList(CustomFieldsService.getCustomFields, 'customFields', i18n.t("Chargement des champs supplémentaires..."), this.props.setCustomFields);
          this.loadList(PriceListsService.getCurrencies, 'currencies', i18n.t("Chargement des devises..."), this.props.setCurrencies);
        }
      } else this.setState({ errorWhileLoading: true }); // Si on ne sait pas récupérer les versions, on affiche une erreur (elles sont récupérées depuis le SW en cas d'offline)
    });

    // Chargement des icônes
    const iconList = Object
      .keys(Icons)
      .filter(key => key !== "fas" && key !== "prefix")
      .map(icon => Icons[icon]);

    library.add(...iconList);
  }

  componentDidUpdate = () => {
    const { updateApp } = this.props;
    const { isUnderMaintenance, subscriptionLoaded } = this.state;
    if (updateApp) throw new Error('updateApp');

    if (isUnderMaintenance && subscriptionLoaded && this.props.activeOrganization?.subscription.shortName !== 'Dev'
      && !window.location.href.includes('login'))
      throw new Error('isUnderMaintenance');
  }

  waitForDatabaseVersions = () => {
    if (this.databaseVersions) return Promise.resolve();
    return new Promise(resolve => {
      const check = () => {
        if (this.databaseVersions) resolve();
        else setTimeout(check, 50);
      };
      check();
    });
  };

  // Bouton « Déconnexion » : garde si des saisies attendent, sinon déconnexion et effacement de la base locale ;
  // synchrone (clé « sync ») pour se déconnecter avant la navigation vers /login. Jamais de base touchée en « se connecter en tant que »
  requestLogout = () => {
    if (this.props.loginAsData) return this.logUser(null);
    if (this.props.sync?.total) this.setState({ logoutGuardCounts: this.props.sync });
    else this.logoutAndClearOfflineData();
  }

  logoutAndClearOfflineData = async (discardPending = false) => {
    const userId = getCurrentUserId();
    const services = getOfflineServices();
    this.setState({ logoutGuardCounts: null });
    this.logUser(null);
    // « sync » peut être périmée (autre onglet, démarrage) : la file est relue ; s'il reste des saisies, la base est gardée
    if (!discardPending) {
      const counts = await services?.outbox.counts().catch(() => null);
      if (!counts || counts.total) return;
    }
    deleteOfflineData(userId).catch(console.error);
  }

  logUser = (token) => {
    if (!token) { // Permet de déconnecter un utilisateur si le paramètre fourni est null (utilisé par le bouton de déconnexion)
      localStorage.removeItem('loginAsData');
      this.props.setLoginAsData(null);
      this.props.setViewProjectAsData(null);
      UsersService.logout();
      this.props.setActiveOrganization(null);
      this.props.setUserInfos(null);
      this.props.setHomeInfos(null);
      this.props.setOrganizationCustomFields(null);
      this.props.setProjectsCustomFields({});
      this.props.setPriceLists(null);
      this.props.setThemes(null);
      new Cookies().remove('token', { path: '/' });
      new Cookies().remove('tokenVersion', { path: '/' });
      RealtimeUtil.restart();
      this.setState({ logged: false, token: '', renderDefaultMap: false, projectListVisible: false });
      this.props.setFilterLists(null);
      this.props.setCustomCharts(null);
      this.props.setProjects(null);
      this.props.setProjectListLoadingStatus({
        rootBaseProjects1Status: 0,
        rootBaseProjects2Status: 0,
        subBaseProjectsStatus: 0,
      });
    } else { // Permet de connecter un utilisateur
      const loginAsData = localStorage.getItem('loginAsData');
      if (loginAsData) this.props.setLoginAsData(JSON.parse(loginAsData));
      let decodedToken = jwtDecode(token);
      const tokenVersion = new Cookies().get('tokenVersion');
      const isNewVersion = tokenVersion && tokenVersion !== AppSettings.getTokenVersion().toString();
      this.props.setUserInfos({ lastName: decodedToken.lastName, firstName: decodedToken.firstName, hasPassword: true });
      // Réécrit à chaque démarrage, jusqu'à l'expiration du jeton au plus : Safari (ITP) efface après 7 jours un cookie écrit
      // par JavaScript, et sans lui la file du hors-ligne et les copies locales deviennent inaccessibles
      const maxAge = decodedToken.exp ? Math.min(31536000, Math.floor(decodedToken.exp - Date.now() / 1000)) : 31536000;
      // tokenVersion avec lui : sans lui, le démarrage suivant supprime le jeton
      if (maxAge > 0) {
        new Cookies().remove('token', { path: '/' });
        new Cookies().set('token', token, { maxAge, path: '/' });
        new Cookies().remove('tokenVersion', { path: '/' });
        new Cookies().set('tokenVersion', AppSettings.getTokenVersion(), { maxAge, path: '/' });
      } else if (!tokenVersion || isNewVersion) {
        new Cookies().remove('tokenVersion', { path: '/' });
        new Cookies().set('tokenVersion', AppSettings.getTokenVersion(), { maxAge: 31536000, path: '/' });
      }
      RealtimeUtil.restart();

      this.setState({ subscriptionLoaded: false }, async () => { // Pour le remettre à false quand on se connecte depuis le formulaire
        // Données de la dernière session en ligne (jamais en « se connecter en tant que »)
        const bootCache = localStorage.getItem('loginAsData') ? null : getOfflineServices()?.bootCache;
        // Base locale illisible (stockage bloqué, base corrompue) : démarrage sans cache, comme une première connexion
        const boot = await bootCache?.load().catch(error => {
          console.error(error);
          return null;
        });
        // Cache appliqué tout de suite hors-ligne, sinon si l'API n'a pas répondu après 2 s ou a échoué
        const applyCacheIfSlow = (request, applyCache) => {
          if (!isApiOnline()) return applyCache();
          let answered = false;
          // Échec rapide (API arrêtée, réseau présent) : cache appliqué sans attendre les 2 s
          request.then(result => {
            answered = true;
            if (!result) applyCache();
          });
          setTimeout(() => { if (!answered) applyCache(); }, 2000);
        };

        // Organisations du pilote : de la dernière session, puis de l'API
        if (boot?.offlinePilotOrganizationIds) {
          setPilotOrganizationIds(boot.offlinePilotOrganizationIds);
          this.setState({ pilotLoadedAt: Date.now() });
        }
        if (bootCache) ParametersService.getOfflinePilot().then(ids => {
          if (!ids) return;
          setPilotOrganizationIds(ids);
          bootCache.save({ offlinePilotOrganizationIds: ids });
          this.setState({ pilotLoadedAt: Date.now() });
        });

        if (this.props.isOnline) {
          const userInfosRequest = UsersService.getUserInfos({ timeout: boot?.userInfos ? 8000 : 0 });
          if (boot?.userInfos) applyCacheIfSlow(userInfosRequest, () => this.applyCachedUserInfos(boot));
          userInfosRequest.then(userInfos => {
            if (userInfos) {
              bootCache?.save({ userInfos });
              const cultureInLocalStorage = localStorage.getItem('i18nextLng');
              if (cultureInLocalStorage && userInfos.language.culture !== cultureInLocalStorage) {
                LanguagesUtil.clearLists();
                window.location.reload();
                i18n.changeLanguage(userInfos.language.culture);
              } else i18n.changeLanguage(userInfos.language.culture);
              this.props.setUserInfos(userInfos);
              if (userInfos.userPreferences?.isToolbarExpanded) this.props.toggleToolbarExpand();
              ThemesUtil.initializeColorVisionType(userInfos.userPreferences?.colorVisionType);

              CustomFieldsService.getOrganizationCustomFields().then(organizationCustomFields => {
                if (organizationCustomFields) {
                  this.props.setOrganizationCustomFields(organizationCustomFields);
                  bootCache?.save({ organizationCustomFields });
                }
              });

              CustomFieldsService.getCustomFieldCategories().then(response => {
                if (response) {
                  const { organizationId, customFieldCategories } = response;
                  this.props.setCustomFieldCategories(organizationId, customFieldCategories);
                  bootCache?.save({ customFieldCategories: response });
                }
              });
            }
          });
        } else if (boot?.userInfos) this.applyCachedUserInfos(boot);

        const params = new URLSearchParams(window.location.search);
        const organizationId = params.get("oid");

        // Réponse de l'API ou, si elle tarde, organisations du cache : l'abonnement n'est chargé qu'une fois
        let isSubscriptionLoaded = false;
        const applyOrganizations = (response) => {
          let organizationIds = false;
          if (response) {
            const { activeOrganization, organizations, newOrganizationIds } = response;
            organizationIds = newOrganizationIds;
            this.props.setOrganizations(organizations);
            this.props.setActiveOrganization(activeOrganization).then(() => {
              if (isSubscriptionLoaded) return;
              isSubscriptionLoaded = true;
              this.setState({ subscriptionLoaded: true })
              this.waitForDatabaseVersions().then(() => this.loadList(TreesService.getEssences, 'essences', i18n.t("Chargement des essences..."), this.props.setEssences));
            });
          }
          this.setState({ logged: true, token, newOrganizationIds: organizationIds });
        };

        const getUserOrganizations = () => {
          const organizationsRequest = OrganizationsService.getUserOrganizations({ timeout: boot?.userOrganizations ? 8000 : 0 });
          let isCacheApplied = false;
          if (boot?.userOrganizations) applyCacheIfSlow(organizationsRequest, () => {
            isCacheApplied = true;
            applyOrganizations(boot.userOrganizations);
          });
          organizationsRequest.then(response => {
            if (response) bootCache?.save({ userOrganizations: { ...response, newOrganizationIds: [] } });
            // Sans réponse, on garde le cache déjà affiché
            if (response || !isCacheApplied) applyOrganizations(response);
          });
        }

        if (organizationId) OrganizationsService.updateActiveOrganization(organizationId).then(getUserOrganizations);
        else getUserOrganizations();
      });
      if (localStorage.getItem('favoriteTools')) this.props.setFavoriteTools(JSON.parse(localStorage.getItem('favoriteTools')));
    }
  }

  // Utilisateur de la dernière session en ligne (la langue vient déjà du localStorage)
  applyCachedUserInfos = (boot) => {
    if (boot.userInfos) {
      this.props.setUserInfos(boot.userInfos);
      ThemesUtil.initializeColorVisionType(boot.userInfos.userPreferences?.colorVisionType);
    }
    if (boot.organizationCustomFields) this.props.setOrganizationCustomFields(boot.organizationCustomFields);
    if (boot.customFieldCategories) this.props.setCustomFieldCategories(boot.customFieldCategories.organizationId, boot.customFieldCategories.customFieldCategories);
  }

  loadList = (loadingFunction, localStorageItem, loadingMessage, reduxSetFunction, sortProperty = null) => {
    const setList = (list) => {
      if (sortProperty) list.sort((a, b) => a[sortProperty].toLowerCase().localeCompare(b[sortProperty].toLowerCase()));
      reduxSetFunction(list);
      this.setState(prevState => ({
        numberOfListsLoaded: prevState.numberOfListsLoaded + 1,
        loadingMessage: loadingMessage
      }), () => {
        if (this.state.numberOfListsLoaded >= this.state.numberOfListsToLoad) {
          localStorage.setItem('versions', JSON.stringify(this.localVersions));
          this.setState({ shouldRenderPage: true });
        }
      });
    }

    // On stocke la version database de la liste en question
    let databaseVersion = this.databaseVersions[localStorageItem] || 0;
    let localStorageVersion = this.localVersions[localStorageItem];
    if (localStorageItem === 'essences' && localStorageVersion)
      databaseVersion = `${this.props.activeOrganization?.essenceLastUpdate || ''}${databaseVersion}`;
    if (localStorage.getItem(localStorageItem) // Si une version locale est renseignée et qu'elle correspond à celle de la base de données
      && localStorageVersion && `${databaseVersion}` === `${localStorageVersion}`)
      setList(JSON.parse(localStorage.getItem(localStorageItem))); // Alors on se sert de la liste stockée dans le localStorage
    else loadingFunction().then(response => { // Sinon on la récupère depuis l'API
      if (response) {
        localStorage.setItem(localStorageItem, JSON.stringify(response)); // On stocke la nouvelle version dans le localStorage
        this.localVersions[localStorageItem] = databaseVersion; // Et on met à jour la version renseignée en local
        setList(response);
      } else if (localStorage.getItem(localStorageItem)) // Si une erreur survient mais qu'une liste est connue dans le localStorage
        setList(JSON.parse(localStorage.getItem(localStorageItem))); // On se sert de l'ancienne liste
      else this.setState({ errorWhileLoading: true }); // Sinon on affiche une erreur car aucune liste ne peut être chargée, donc l'application ne sera pas fonctionnelle
    });
  }

  changeActiveItem = (newState) => this.setState({ activeItem: newState });
  showProjectList = (projectListVisible, { renderDefaultMap = true, openProjectCreation = false, showCurrentFolder = false } = {}) => this.setState({ projectListVisible, renderDefaultMap, openProjectCreation, showCurrentFolder });
  manageAccount = () => this.setState({ activeItem: 'manageAccount' });
  showAIWarning = (isVisible) => this.setState({ isAIWarningVisible: isVisible });
}

const mapStateToProps = (state) => {
  return {
    project: state.project,
    projects: state.projects,
    essences: state.essences,
    isKeyboardOpen: state.isKeyboardOpen,
    isDarkTheme: state.isDarkTheme,
    activeOrganization: state.activeOrganization,
    isOnline: state.isOnline,
    userInfos: state.userInfos,
    tutorialTour: state.tutorialTour,
    updateApp: state.updateApp,
    photosGalleries: state.photosGalleries,
    filesGalleries: state.filesGalleries,
    loginAsData: state.loginAsData,
    sync: state.sync,
    exitFormWithChanges: state.exitFormWithChanges,
    currentFolderState: state.currentFolderState
  };
};

const mapDispatchToProps = {
  setCurrentFolderState,
  setUserInfos,
  setActiveOrganization,
  setOrganizations,
  setEssences,
  setCoverTypes,
  setInteractions,
  setEvents,
  setMicroHabitats,
  setRootSymptoms,
  setCollarSymptoms,
  setTrunkSymptoms,
  setBranchSymptoms,
  setLeafSymptoms,
  setPathogens,
  setPests,
  setEpiphytes,
  setVigors,
  setDeathCauses,
  setHealthReviews,
  setOntogenicStages,
  setArchiStages,
  setArchiStates,
  setRisks,
  setTippingRisks,
  setOrganCalibers,
  setTargets,
  setTreePorts,
  setPlantationTypes,
  setPlantationCoefficients,
  setSituationCoefficients,
  setPatrimonialCoefficients,
  setActions,
  setIsKeyboardOpen,
  setIsDarkTheme,
  setCustomCharts,
  setFilterLists,
  setSpaceFunctions,
  setSpaceTypes,
  setSpaceTypeCategories,
  setRunoffCoefficients,
  setManagementClasses,
  setDominantCompositions,
  setConditions,
  setFurnitureTypes,
  setFormulas,
  setProjects,
  setProjections,
  setFavoriteTools,
  setDefaultFieldCategories,
  setCustomFields,
  setOrganizationCustomFields,
  setCustomFieldCategories,
  setProjectsCustomFields,
  setPriceLists,
  setThemes,
  setPhotosGalleries,
  setFilesGalleries,
  setCurrencies,
  setHomeInfos,
  setLoginAsData,
  setViewProjectAsData,
  setProjectListLoadingStatus,
  setUpdateApp,
  toggleToolbarExpand
};

export default withOrientationChange(connect(mapStateToProps, mapDispatchToProps)(App));